import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { dealLineItemsApi } from '@/lib/api'
import { useNotificationStore } from './notifications'
import type { DealLineItem } from '@/types'

export interface LineItemsSummary {
  subtotal: number
  totalDiscount: number
  totalTax: number
  totalSetupFees: number
  grandTotal: number
  lineItemCount: number
}

export const useDealLineItemsStore = defineStore('dealLineItems', () => {
  const lineItems = ref<DealLineItem[]>([])
  const summary = ref<LineItemsSummary | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const notifications = useNotificationStore()

  // Computed
  const lineItemCount = computed(() => lineItems.value.length)

  const oneTimeItems = computed(() =>
    lineItems.value.filter(item => item.pricingType === 'one_time')
  )

  const recurringItems = computed(() =>
    lineItems.value.filter(item => item.pricingType === 'recurring')
  )

  const oneTimeTotal = computed(() =>
    oneTimeItems.value.reduce((sum, item) => sum + item.total, 0)
  )

  const recurringMonthlyTotal = computed(() =>
    recurringItems.value
      .filter(item => item.billingInterval === 'monthly')
      .reduce((sum, item) => sum + item.total, 0)
  )

  const recurringYearlyTotal = computed(() =>
    recurringItems.value
      .filter(item => item.billingInterval === 'yearly')
      .reduce((sum, item) => sum + item.total, 0)
  )

  // Actions
  async function fetchLineItems(dealId: string): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await dealLineItemsApi.list(dealId) as any
      // apiClient.get unwraps axios data, so response = { data: [...], summary: {...} }
      lineItems.value = response.data || []
      summary.value = response.summary
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch line items'
      notifications.error('Failed to load line items')
    } finally {
      loading.value = false
    }
  }

  async function addLineItem(dealId: string, data: any): Promise<DealLineItem | null> {
    loading.value = true
    error.value = null

    try {
      const response = await dealLineItemsApi.add(dealId, data)
      const newItem = response.data
      // Refetch to get updated summary
      await fetchLineItems(dealId)
      notifications.success('Line item added successfully')
      return newItem
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to add line item'
      notifications.error(err.response?.data?.message || 'Failed to add line item')
      return null
    } finally {
      loading.value = false
    }
  }

  async function updateLineItem(
    dealId: string,
    lineItemId: string,
    data: any,
  ): Promise<DealLineItem | null> {
    loading.value = true
    error.value = null

    try {
      const response = await dealLineItemsApi.update(dealId, lineItemId, data)
      const updatedItem = response.data
      // Refetch to get updated summary
      await fetchLineItems(dealId)
      notifications.success('Line item updated successfully')
      return updatedItem
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to update line item'
      notifications.error(err.response?.data?.message || 'Failed to update line item')
      return null
    } finally {
      loading.value = false
    }
  }

  async function removeLineItem(dealId: string, lineItemId: string): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      await dealLineItemsApi.remove(dealId, lineItemId)
      // Refetch to get updated summary
      await fetchLineItems(dealId)
      notifications.success('Line item removed successfully')
      return true
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to remove line item'
      notifications.error(err.response?.data?.message || 'Failed to remove line item')
      return false
    } finally {
      loading.value = false
    }
  }

  function clearLineItems(): void {
    lineItems.value = []
    summary.value = null
    error.value = null
  }

  return {
    // State
    lineItems,
    summary,
    loading,
    error,

    // Computed
    lineItemCount,
    oneTimeItems,
    recurringItems,
    oneTimeTotal,
    recurringMonthlyTotal,
    recurringYearlyTotal,

    // Actions
    fetchLineItems,
    addLineItem,
    updateLineItem,
    removeLineItem,
    clearLineItems,
  }
})
